import { useState } from 'react'
import { IconButton, Input, type InputProps } from '@asus-common/fe-ui-shadcn'
import {
  MdErrorOutline,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
} from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import type { FieldError } from 'react-hook-form'

type PasswordInputProps = Omit<InputProps, 'type' | 'customEndIcon'> & {
  error?: FieldError
  onChange: (...event: unknown[]) => void
}

export function PasswordInput({
  error,
  onChange,
  ...props
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false)
  const { t } = useTranslation(['auth-common', 'common', 'login'])

  return (
    <Input
      type={showPassword ? 'text' : 'password'}
      size="md"
      maxLength={100}
      invalid={!!error}
      errorMessage={error?.message ? t(error.message) : undefined}
      onCopy={e => e.preventDefault()}
      onChange={onChange}
      customEndIcon={
        <span className="flex items-center gap-1">
          {error && (
            <MdErrorOutline
              className="text-error"
              data-slot="end-icon"
              data-testid="error-icon"
              aria-hidden
            />
          )}
          <IconButton
            className="text-muted-foreground p-0 hover:bg-transparent"
            type="button"
            variant="ghost"
            status="secondary"
            size="sm"
            aria-label={
              showPassword
                ? t('auth-common:password_input.hide_password')
                : t('auth-common:password_input.show_password')
            }
            icon={
              showPassword ? (
                <MdOutlineVisibility />
              ) : (
                <MdOutlineVisibilityOff />
              )
            }
            onClick={() => setShowPassword(v => !v)}
          />
        </span>
      }
      {...props}
    />
  )
}
